// @givmo/crm — the segment picker, built to be rented (CRM-5; Email Campaign plan §2.1, D-EMC-10).
//
// A list of segments, each a checkbox with its name and how many people are in it now. The email
// program's audience panel rents it: a person with the CRM sees it as it is; a person without the
// CRM (Fundraisers access only) sees the same names and counts, never the contacts, and a line
// saying they can see the contacts with access to the CRM, with the host's request door beside it.
//
// The picker reads nothing itself. Its rows arrive as props: from the CRM's own segment list
// (`useCrmSegmentPickerRows`, for someone who holds the CRM), or from the renting module's own read
// of names and counts (for someone who does not). A row is a NAME and a COUNT, nothing else, so a
// host cannot hand a member to someone without the CRM through this component.

import { useQuery } from '@tanstack/react-query';
import { Button, Checkbox, cn, testIdProp } from '@givmo/design-system';
import { displayGroupedCount } from './crm-decode';
import { crmSegmentsOptions } from './crm-queries';

export interface CrmSegmentPickerRow {
  id: string;
  name: string;
  /** How many people the segment holds now; null when the count could not be read. */
  count: number | null;
}

export interface CrmSegmentPickerProps {
  rows: readonly CrmSegmentPickerRow[];
  selected: readonly string[];
  onChange: (selected: string[]) => void;
  /**
   * Whether this person can see the CRM's contacts. False: names and counts only, and the line that
   * they can see the contacts with access to the CRM (D-EMC-10).
   */
  contactsVisible: boolean;
  /** The host's request door for the CRM. Absent, no button is offered (the host has none to open). */
  onRequestAccess?: () => void;
  /** A request this person already sent and nobody has answered yet. */
  accessRequestPending?: boolean;
  /** The section's title. */
  title?: string;
  /** A line under the title, when the host has one (the email page: when its counts are taken). */
  note?: string;
  /** How a row's count reads. Absent, the grouped number (`1,184`, or `—` when unread). */
  formatCount?: (count: number | null) => string;
  testIdPrefix?: string;
}

export function CrmSegmentPicker({
  rows,
  selected,
  onChange,
  contactsVisible,
  onRequestAccess,
  accessRequestPending = false,
  title = 'Segments',
  note,
  formatCount = displayGroupedCount,
  testIdPrefix = 'crm-segment-picker',
}: CrmSegmentPickerProps) {
  const chosen = new Set(selected);
  const toggle = (id: string, checked: boolean) => {
    const next = rows.filter((row) => (row.id === id ? checked : chosen.has(row.id)));
    onChange(next.map((row) => row.id));
  };

  return (
    <section className="flex flex-col gap-12" {...testIdProp(testIdPrefix)}>
      <h3 className="font-sans text-s13 font-w600 tracking-givmo text-text-strong">{title}</h3>
      {note === undefined ? null : (
        <p
          className="-mt-8 font-sans text-s13 leading-h15 tracking-givmo text-text-secondary"
          {...testIdProp(`${testIdPrefix}-note`)}
        >
          {note}
        </p>
      )}
      {rows.length === 0 ? (
        <p
          className="font-sans text-s13 leading-h15 tracking-givmo text-text-secondary"
          {...testIdProp(`${testIdPrefix}-empty`)}
        >
          No segments yet.
        </p>
      ) : (
        <div className="flex flex-col">
          {rows.map((row) => (
            <label
              key={row.id}
              className="flex items-center gap-10 py-6 font-sans text-s14"
              {...testIdProp(`${testIdPrefix}-row-${row.id}`)}
            >
              <Checkbox
                appearance="saas"
                size={18}
                checked={chosen.has(row.id)}
                onCheckedChange={(checked) => toggle(row.id, checked === true)}
              />
              <span className={cn('min-w-0 flex-1 truncate text-text-strong')}>{row.name}</span>
              <span className="tabular-nums text-text-tertiary">{formatCount(row.count)}</span>
            </label>
          ))}
        </div>
      )}
      {contactsVisible ? null : (
        <div
          className="flex flex-col gap-10 rounded-lg border border-border-subtle px-14 py-12"
          {...testIdProp(`${testIdPrefix}-no-access`)}
        >
          <p className="font-sans text-s13 leading-h15 tracking-givmo text-text-secondary">
            You can see these contacts with access to the CRM.
          </p>
          {onRequestAccess === undefined ? null : (
            <div>
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                disabled={accessRequestPending}
                onClick={onRequestAccess}
                aria-label={
                  accessRequestPending ? 'Access to the CRM requested' : 'Request access to the CRM'
                }
                testId={`${testIdPrefix}-request-access`}
              >
                {accessRequestPending ? 'Requested' : 'Request access'}
              </Button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

/**
 * The picker's rows from the CRM's own segment list, for a person who holds the CRM. `null` while
 * the list has not been read (loading, failed, or the module dark).
 */
export function useCrmSegmentPickerRows(orgRef: string): CrmSegmentPickerRow[] | null {
  const segmentsQuery = useQuery(crmSegmentsOptions(orgRef));
  if (!segmentsQuery.isSuccess) return null;
  return segmentsQuery.data.segments.map((segment) => ({
    id: segment.id,
    name: segment.name,
    count: segment.count,
  }));
}
